import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@gumahub/authentication';
import { PageLoading } from '@gumahub/shared';
import MainLayout from './layouts/MainLayout';
import DashboardPage from './pages/DashboardPage';

// Import LoginPage wrapper to ensure i18n is ready
import LoginPageWrapper from './components/LoginPageWrapper';

// Lazy load module pages
const EnterprisePage = React.lazy(() => import('./pages/modules/EnterprisePage'));
const OrdersPage = React.lazy(() => import('./pages/modules/OrdersPage'));
const ShipmentsPage = React.lazy(() => import('./pages/modules/ShipmentsPage'));
const BidsPage = React.lazy(() => import('./pages/modules/BidsPage'));
const EmployeesPage = React.lazy(() => import('./pages/modules/EmployeesPage'));
const ExpensesPage = React.lazy(() => import('./pages/modules/ExpensesPage'));
const ExpensePage = React.lazy(() => import('./pages/modules/ExpensePage'));
const TravelPage = React.lazy(() => import('./pages/modules/TravelPage'));
const ReportsModulePage = React.lazy(() => import('./pages/modules/ReportsPage'));
const AbsencePage = React.lazy(() => import('./pages/modules/AbsencePage'));
const TrainingPage = React.lazy(() => import('./pages/modules/TrainingPage'));
const PositionPage = React.lazy(() => import('./pages/modules/PositionPage'));
const SurveyPage = React.lazy(() => import('./pages/modules/SurveyPage'));
const DocumentsPage = React.lazy(() => import('./pages/modules/DocumentsPage'));
const ServicePage = React.lazy(() => import('./pages/modules/ServicePage'));
const PublicSurveyPage = React.lazy(() => import('./pages/PublicSurveyPage'));
const TimePage = React.lazy(() => import('./pages/modules/TimePage'));
const PerformancePage = React.lazy(() => import('./pages/modules/PerformancePage'));

// Training pages
const TrainingCatalogPage = React.lazy(() => import('./pages/training/TrainingCatalogPage'));
const MyTrainingPage = React.lazy(() => import('./pages/training/MyTrainingPage'));
const CourseDetailPage = React.lazy(() => import('./pages/training/CourseDetailPage'));
const CourseManagementPage = React.lazy(() => import('./pages/training/CourseManagementPage'));
const SessionManagementPage = React.lazy(() => import('./pages/training/SessionManagementPage'));
const CertificatesPage = React.lazy(() => import('./pages/training/CertificatesPage'));

// Admin pages
const UsersPage = React.lazy(() => import('./pages/admin/UsersPage'));
const ClientModulesPage = React.lazy(() => import('./pages/admin/ClientModulesPage'));
const BrandingManagementPage = React.lazy(() => import('./pages/admin/BrandingManagementPage'));
const IntegrationSettingsPage = React.lazy(() => import('./pages/admin/IntegrationSettingsPage'));
const IntegrationApiDocsPage = React.lazy(() => import('./pages/admin/IntegrationApiDocsPage'));

// Settings pages
const PackingInstructionsPage = React.lazy(() => import('./pages/settings/PackingInstructionsPage'));
const PartsCrossRefPage = React.lazy(() => import('./pages/settings/PartsCrossRefPage'));
const HUTypesPage = React.lazy(() => import('./pages/settings/HUTypesPage'));
const AbsenceTypesPage = React.lazy(() => import('./pages/settings/AbsenceTypesPage'));
const ExpenseTypesPage = React.lazy(() => import('./pages/settings/ExpenseTypesPage'));
const EmploymentTypesPage = React.lazy(() => import('./pages/settings/EmploymentTypesPage'));
const CostCentersPage = React.lazy(() => import('./pages/settings/CostCentersPage'));
const HolidayCalendarPage = React.lazy(() => import('./pages/settings/HolidayCalendarPage'));
const FxRatesPage = React.lazy(() => import('./pages/settings/FxRatesPage'));
const HRDashboardPage = React.lazy(() => import('./pages/hr/HRDashboardPage'));
const SelfServicePage = React.lazy(() => import('./pages/me/SelfServicePage'));

// Account pages
const PreferencesPage = React.lazy(() => import('./pages/account/PreferencesPage'));

// Branding pages
const BrandedLoginPage = React.lazy(() => import('./pages/BrandedLoginPage'));
const BrandingSetupPage = React.lazy(() => import('./pages/settings/BrandingSetupPage'));

const App: React.FC = () => {
  return (
    <React.Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login/:slug" element={<BrandedLoginPage />} />
        <Route path="/login" element={<LoginPageWrapper />} />

        {/* Public tokenized survey (external respondents, no auth) */}
        <Route path="/s/:token" element={<PublicSurveyPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />

          {/* Enterprise Module */}
          <Route
            path="enterprise/*"
            element={
              <ProtectedRoute moduleId="ENT">
                <EnterprisePage />
              </ProtectedRoute>
            }
          />

          {/* Order Module */}
          <Route
            path="orders/*"
            element={
              <ProtectedRoute moduleId="ORD">
                <OrdersPage />
              </ProtectedRoute>
            }
          />

          {/* Shipment Module */}
          <Route
            path="shipments/*"
            element={
              <ProtectedRoute moduleId="SHP">
                <ShipmentsPage />
              </ProtectedRoute>
            }
          />

          {/* Bid Management Module */}
          <Route
            path="bids/*"
            element={
              <ProtectedRoute moduleId="BID">
                <BidsPage />
              </ProtectedRoute>
            }
          />

          {/* Employee Organization Module */}
          <Route
            path="employees/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <EmployeesPage />
              </ProtectedRoute>
            }
          />

          {/* Expense Module */}
          <Route
            path="expenses/*"
            element={
              <ProtectedRoute moduleId="EXP">
                <ExpensesPage />
              </ProtectedRoute>
            }
          />

          {/* Travel Module */}
          <Route
            path="travel/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <TravelPage />
              </ProtectedRoute>
            }
          />

          {/* Expense Module (new) */}
          <Route
            path="expense/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <ExpensePage />
              </ProtectedRoute>
            }
          />

          {/* Absence Module */}
          <Route
            path="absence/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <AbsencePage />
              </ProtectedRoute>
            }
          />

          {/* Position & Grade Module */}
          <Route
            path="positions/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <PositionPage />
              </ProtectedRoute>
            }
          />

          {/* Time & Attendance Module */}
          <Route
            path="time/*"
            element={
              <ProtectedRoute moduleId="TIME">
                <TimePage />
              </ProtectedRoute>
            }
          />

          {/* Performance Module (goals & OKRs) */}
          <Route
            path="performance/*"
            element={
              <ProtectedRoute moduleId="EMP">
                <PerformancePage />
              </ProtectedRoute>
            }
          />

          {/* Survey & Feedback Module */}
          <Route
            path="surveys/*"
            element={
              <ProtectedRoute moduleId="SRV">
                <SurveyPage />
              </ProtectedRoute>
            }
          />

          {/* Training Module */}
          <Route
            path="training"
            element={
              <ProtectedRoute moduleId="EMP">
                <TrainingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/catalog"
            element={
              <ProtectedRoute moduleId="EMP">
                <TrainingCatalogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/my-training"
            element={
              <ProtectedRoute moduleId="EMP">
                <MyTrainingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/certificates"
            element={
              <ProtectedRoute moduleId="EMP">
                <CertificatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/courses/:courseId"
            element={
              <ProtectedRoute moduleId="EMP">
                <CourseDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/admin/courses"
            element={
              <ProtectedRoute moduleId="EMP">
                <CourseManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="training/admin/sessions"
            element={
              <ProtectedRoute moduleId="EMP">
                <SessionManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Document Management Module */}
          <Route
            path="documents/*"
            element={
              <ProtectedRoute moduleId="DOC">
                <DocumentsPage />
              </ProtectedRoute>
            }
          />

          {/* Service Module (field service / service desk) */}
          <Route
            path="service/*"
            element={
              <ProtectedRoute moduleId="SVC">
                <ServicePage />
              </ProtectedRoute>
            }
          />

          {/* Reports Module */}
          <Route
            path="reports/*"
            element={
              <ProtectedRoute moduleId="RPT">
                <ReportsModulePage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes (Platform Admin Only) */}
          <Route path="admin/modules" element={<ClientModulesPage />} />
          <Route path="admin/users" element={<UsersPage />} />
          <Route path="admin/branding" element={<BrandingManagementPage />} />
          <Route path="admin/integrations" element={<IntegrationSettingsPage />} />
          <Route path="admin/integrations/api-docs" element={<IntegrationApiDocsPage />} />

          {/* Account Routes */}
          <Route path="settings/preferences" element={<PreferencesPage />} />

          {/* Settings Routes - Order */}
          <Route
            path="settings/order/parts-cross-ref"
            element={
              <ProtectedRoute moduleId="ORD">
                <PartsCrossRefPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Routes - Shipment */}
          <Route
            path="settings/shipment/packing-instructions"
            element={
              <ProtectedRoute moduleId="SHP">
                <PackingInstructionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/shipment/hu-types"
            element={
              <ProtectedRoute moduleId="SHP">
                <HUTypesPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Routes - HR */}
          <Route
            path="settings/hr/absence-types"
            element={
              <ProtectedRoute moduleId="EMP">
                <AbsenceTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/hr/expense-types"
            element={
              <ProtectedRoute moduleId="EMP">
                <ExpenseTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/hr/employment-types"
            element={
              <ProtectedRoute moduleId="EMP">
                <EmploymentTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/hr/cost-centers"
            element={
              <ProtectedRoute moduleId="EMP">
                <CostCentersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/hr/holidays"
            element={
              <ProtectedRoute moduleId="EMP">
                <HolidayCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/expense/fx-rates"
            element={
              <ProtectedRoute moduleId="EMP">
                <FxRatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/dashboard"
            element={
              <ProtectedRoute moduleId="EMP">
                <HRDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="me"
            element={
              <ProtectedRoute moduleId="EMP">
                <SelfServicePage />
              </ProtectedRoute>
            }
          />

          {/* Settings Routes - Company Branding */}
          <Route
            path="settings/company/branding"
            element={
              <ProtectedRoute moduleId="ENT">
                <BrandingSetupPage />
              </ProtectedRoute>
            }
          />

          {/* Legacy redirect for old packing instructions URL */}
          <Route path="settings/packing-instructions" element={<Navigate to="/settings/shipment/packing-instructions" replace />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
};

export default App;
